/* Gift Reggie hardcoded storefront strings not exposed in Preferences → Translations.
 * The app owns validation and submission. Form enhancement preserves its inputs and handlers.
 * Configurable translations (including shipping labels) remain in the app admin.
 */
(() => {
  'use strict';

  const root = document.querySelector('.giftreggie-front');
  if (!root || !document.documentElement.lang.toLowerCase().startsWith('de')) return;

  const countryNames = new Map();
  if (typeof Intl.DisplayNames === 'function') {
    const english = new Intl.DisplayNames(['en'], { type: 'region' });
    const german = new Intl.DisplayNames(['de'], { type: 'region' });
    // Resolve the app's English labels without changing its country-name values.
    for (let a = 65; a <= 90; a += 1) {
      for (let b = 65; b <= 90; b += 1) {
        const code = String.fromCharCode(a, b);
        const name = english.of(code);
        if (name !== code) countryNames.set(name, german.of(code));
      }
    }
  }
  Object.entries({
    'Macedonia': 'Nordmazedonien',
    'Netherlands Antilles': 'Niederländische Antillen',
    'Swaziland': 'Eswatini',
    'Cape Verde': 'Kap Verde',
    'Czech Republic': 'Tschechien',
    'Turkey': 'Türkei',
    'East Timor': 'Timor-Leste',
    'Aland Islands': 'Ålandinseln',
  }).forEach(([english, german]) => countryNames.set(english, german));

  function replaceTextNodes(element, transform) {
    element.childNodes.forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) return;
      const next = transform(node.textContent);
      if (next !== node.textContent) node.textContent = next;
    });
  }

  function simplifyForm() {
    const profile = root.querySelector('.registry-profile');
    if (!profile || profile.dataset.berndorfSimplified) return;
    const field = (name) => profile.querySelector(`[name="${name}"]`);
    const owner = field('registrant-first');
    const second = field('coregistrant-first');
    const email = field('contact-email');
    const address = field('contact-address');
    if (!owner || !second || !email || !address) return;
    profile.dataset.berndorfSimplified = 'true';
    const form = profile.closest('form');
    const personBlock = owner.closest('.registry-profile-block');
    const contactBlock = address.closest('.registry-profile-block');
    const secondPerson = second.parentElement.parentElement;
    const hide = (element) => element?.classList.add('berndorf-form-hidden');
    const disclosure = (text) => {
      const element = document.createElement('details');
      const summary = document.createElement('summary');
      summary.textContent = text;
      element.append(summary);
      return element;
    };
    const label = (input, text) => {
      replaceTextNodes(input.parentElement, () => '');
      input.parentElement.prepend(document.createTextNode(`${text} `));
      input.setAttribute('aria-label', text);
    };

    profile.querySelector('.giftreggie-band > h4').textContent = 'Geschenkeliste';
    const advanced = disclosure('Weitere Einstellungen');
    advanced.className = 'registry-profile-block berndorf-form-advanced';
    profile.append(advanced);
    const secondDetails = disclosure('Zweite Person (optional)');
    secondDetails.className = 'berndorf-form-secondary';
    hide(secondPerson.querySelector('h4'));
    secondDetails.append(secondPerson);
    advanced.append(secondDetails);
    secondDetails.open = Boolean(second.value || field('coregistrant-last').value);

    const protection = profile.querySelector('#registry-protection')?.closest('.registry-profile-block');
    if (protection) {
      protection.className = 'berndorf-form-subsection';
      advanced.append(protection);
    }
    const managers = field('email-managers');
    if (managers) advanced.append(managers.closest('p'));
    const shipping = profile.querySelector('#before-shipping-selector')?.closest('.registry-profile-block');
    if (shipping) {
      shipping.className = 'berndorf-form-subsection berndorf-form-addresses';
      shipping.querySelector('h4').textContent = 'Abweichende Lieferadressen';
      shipping.querySelectorAll('select[id$="shipping-selector"] option[value="above"]').forEach((option) => {
        option.textContent = 'Lieferadresse aus Kontakt & Lieferung';
      });
      shipping.querySelectorAll('select[id$="shipping-selector"] option[value="below"]').forEach((option) => {
        option.textContent = 'Andere Lieferadresse';
      });
      advanced.append(shipping);
    }

    // Move original controls, not copies: preserve app listeners and submitted names.
    contactBlock.classList.add('berndorf-form-contact');
    contactBlock.querySelector(':scope > h4').textContent = 'Kontakt & Lieferung';
    const grid = document.createElement('div');
    grid.className = 'berndorf-form-grid';
    [
      'registrant-first', 'registrant-last', 'contact-email', 'contact-daytime',
      'contact-address', 'contact-postal', 'contact-city', 'contact-country', 'contact-province',
    ].forEach((name) => grid.append(field(name).closest('p')));
    contactBlock.append(grid);
    hide(personBlock);
    contactBlock.querySelectorAll(':scope > span').forEach(hide);
    const referral = field('about-type');
    if (referral?.options.length === 1 && referral.options[0].value === 'N/A') referral.value = 'N/A';
    hide(referral?.closest('p'));
    ['registrant-title', 'coregistrant-title'].forEach((name) => hide(field(name)?.closest('p')));
    label(field('contact-daytime'), 'Telefonnummer (optional)');
    field('contact-daytime').type = 'tel';
    field('contact-daytime').autocomplete = 'tel';
    label(address, 'Straße und Hausnummer');
    address.autocomplete = 'street-address';
    email.type = 'email';
    email.autocomplete = 'email';
    // The app supplies the account email as its placeholder on creation.
    if (!email.value && email.placeholder.includes('@')) {
      email.defaultValue = email.placeholder;
      email.value = email.placeholder;
    }
    const confirmation = contactBlock.querySelector('input:not([name])');
    const syncEmail = () => { if (confirmation) confirmation.value = email.value; };
    email.addEventListener('input', syncEmail);
    form?.addEventListener('submit', syncEmail, true);
    syncEmail();
    const evening = field('contact-evening');
    if (!field('contact-daytime').value && evening?.value) field('contact-daytime').value = evening.value;

    // Keep original country values for the app. Existing non-Austrian addresses
    // remain editable rather than silently converting saved customer data.
    ['contact', 'before', 'after'].forEach((prefix) => {
      const country = field(`${prefix}-country`);
      if (!country) return;
      if (prefix !== 'contact' && !field(`${prefix}-address`)?.value) country.value = 'Austria';
      const fixed = document.createElement('span');
      fixed.textContent = 'Österreich';
      fixed.className = 'berndorf-form-country';
      country.after(fixed);
      const refreshCountry = () => {
        const austria = country.value === 'Austria';
        country.classList.toggle('berndorf-form-hidden', austria);
        fixed.hidden = !austria;
        field(`${prefix}-province`)?.closest('p').classList.toggle('berndorf-form-hidden', austria);
      };
      country.addEventListener('change', refreshCountry);
      refreshCountry();
    });
    // Open settings that already contain data so owners can discover and edit it.
    advanced.open = secondDetails.open || Boolean(profile.querySelector('#registry-protection:checked')) ||
      Boolean(managers?.checked) || Boolean(field('before-address')?.value || field('after-address')?.value);
    profile.querySelectorAll('input, select, textarea').forEach((input) => {
      if (!input.getAttribute('aria-label')) {
        const text = [...input.parentElement.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE)
          .map((node) => node.textContent.trim()).filter(Boolean).join(' ');
        if (text) input.setAttribute('aria-label', text);
      }
    });
    form?.addEventListener('invalid', (event) => {
      let element = event.target.parentElement;
      while (element && element !== form) {
        if (element.tagName === 'DETAILS') element.open = true;
        element = element.parentElement;
      }
    }, true);
  }

  function localize() {
    root.querySelectorAll('select[name$="-country"] option').forEach((option) => {
      const translated = countryNames.get(option.textContent.trim());
      if (translated && translated !== option.textContent) option.textContent = translated;
    });

    const managers = root.querySelector('input[name="email-managers"]');
    if (managers) {
      const text = 'Benachrichtigungen zusätzlich an die Personen senden, die diese Geschenkeliste verwalten';
      replaceTextNodes(managers.parentElement, (value) => value.replace(
        'Send notification emails to registry managers (in addition to contact email)', text
      ));
      managers.setAttribute('aria-label', text);
    }

    root.querySelectorAll('#about-type option').forEach((option) => {
      if (option.textContent.trim() === 'N/A') {
        option.value = 'N/A';
        option.textContent = 'Keine Angabe';
      }
    });

    [
      ['.giftreggie-share-email-details-to', 'E-Mail-Adressen der Empfängerinnen und Empfänger'],
      ['.giftreggie-share-email-details-subject', 'Betreff'],
      ['.giftreggie-share-email-details-body', 'Nachricht'],
    ].forEach(([selector, label]) => {
      root.querySelectorAll(selector).forEach((field) => {
        field.placeholder = label;
        field.setAttribute('aria-label', label);
      });
    });
    root.querySelectorAll('.giftreggie-share-email-remaining').forEach((element) => {
      replaceTextNodes(element, (text) => text.replace(
        /(\d+)\/(\d+) Shares Used/, '$1 von $2 Freigaben verwendet'
      ));
    });

    const date = root.querySelector('input[name="event-date"]');
    if (date) {
      replaceTextNodes(date.parentElement, (text) => text.replace(/DD\/MM\/YYYY/g, 'TT/MM/JJJJ'));
      if (date.placeholder === 'DD/MM/YYYY') date.placeholder = 'TT/MM/JJJJ';
      date.setAttribute('aria-label', 'Datum des Anlasses (TT/MM/JJJJ)');
    }

    root.querySelectorAll('.giftreggie-registry-summary td, .giftreggie-registry-summary p').forEach((cell) => {
      replaceTextNodes(cell, (text) => {
        if (text.trim() === 'Open') return text.replace('Open', 'Offen');
        if (text.trim() === 'Closed') return text.replace('Closed', 'Geschlossen');
        return text.replace(/(:\s*)Open\s*$/, '$1Offen').replace(/(:\s*)Closed\s*$/, '$1Geschlossen');
      });
    });

    // Gift Reggie's money filter can produce 1,298,00 (or US 1,298.00).
    // Correct only these unambiguous grouped values; German prices stay untouched.
    root.querySelectorAll('.money, gr-storefront-field[data-field="variant.price"]').forEach((element) => {
      replaceTextNodes(element, (text) => text.replace(
        /(\d{1,3}(?:,\d{3})+)[,.](\d{2})(?=\s*(?:€|EUR)?\s*$)/g,
        (_, whole, cents) => `${whole.replace(/,/g, '.')},${cents}`
      ));
    });

    root.querySelectorAll('input[type="file"][name="registry-image"]').forEach((input) => {
      if (input.dataset.berndorfLocalized) return;
      input.dataset.berndorfLocalized = 'true';
      input.classList.add('visually-hidden');
      input.tabIndex = -1;
      input.setAttribute('aria-label', 'Bild der Geschenkeliste auswählen');
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = 'Bild auswählen';
      button.addEventListener('click', () => input.click());
      const filename = document.createElement('span');
      filename.className = 'berndorf-giftlist-filename';
      filename.setAttribute('aria-live', 'polite');
      filename.textContent = 'Keine Datei ausgewählt';
      input.addEventListener('change', () => {
        filename.textContent = input.files.length ? input.files[0].name : 'Keine Datei ausgewählt';
      });
      input.after(button, filename);
    });
  }

  // Abgemeldet zeigt die App nur einen unterstrichenen Satz, der zugleich der
  // Link ist. Daraus werden ein Hinweis und zwei Knoepfe. Beide fuehren auf
  // dieselbe Adresse: die neuen Kundenkonten kennen keine getrennte
  // Registrierung, das Konto entsteht bei der ersten Anmeldung.
  function loginButtons() {
    root.querySelectorAll('.giftreggie-manage-non-customer').forEach((box) => {
      if (box.dataset.berndorfLogin === 'true') return;
      const link = box.querySelector('a[href]');
      if (!link) return;
      box.dataset.berndorfLogin = 'true';
      const href = link.getAttribute('href');
      const hint = document.createElement('p');
      hint.className = 'berndorf-giftlist-login__hint';
      hint.textContent = 'Um eine Geschenkeliste anzulegen oder zu verwalten, melden Sie sich bitte an. ' +
        'Noch kein Kundenkonto? Es wird bei der ersten Anmeldung mit Ihrer E-Mail-Adresse angelegt.';
      const actions = document.createElement('div');
      actions.className = 'berndorf-giftlist-login__actions';
      [['Anmelden', 'berndorf-button'], ['Registrieren', 'berndorf-button berndorf-button--outline']]
        .forEach(([label, className]) => {
          const button = document.createElement('a');
          button.href = href;
          button.className = className;
          button.textContent = label;
          actions.append(button);
        });
      box.replaceChildren(hint, actions);
    });
  }

  const observer = new MutationObserver(() => {
    observer.disconnect();
    localize();
    loginButtons();
    observe();
  });
  function observe() {
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  }
  localize();
  loginButtons();
  // Gift Reggie initializes countries and customer fields asynchronously.
  // Enhance after its own ready queue, not merely after the browser load event.
  if (typeof window.displayModal === 'function') simplifyForm();
  else if (Array.isArray(window.jqReady)) window.jqReady.push(simplifyForm);
  observe();
})();
